import React, { useEffect, useState } from "react";
import { Button } from "antd";
import axiosInstance from "../../../utils/axiosInstance";
import ViewTestAndControlMarket from "../../measurement/geo-lift/components/ViewTestAndControlMarket";

interface MarketData {
  Holdout: number;
  ID: number;
  location: string;
  duration: number;
  EffectSize: number;
  Power: number;
  AvgScaledL2Imbalance: number;
  Investment: number;
  AvgATT: number;
  Average_MDE: number;
  ProportionTotal_Y: number;
  rank: number;
  abs_lift_in_perc: number;
  correlation: number;
}

interface StepPowerAnalysisProps {
  jobConfigId: string;
  jobName?: string;
  isSales: boolean;
  onNext: () => void;
  onPrevious: () => void;
}

// This step is the real Measurement "Power Analysis" page, reused verbatim -
// per explicit instruction, it must be the exact same page (same
// MarketAnalysisDashboard block, same gradient results table, same 3-chart
// modal), not a Causal-Loop-specific reinterpretation. The only Causal-Loop
// code here is the data plumbing GeoLiftStudy.tsx normally does around it
// (deriving testMarkets/marketData from /api/geo-lift/output/{jobConfigId} -
// see GeoLiftStudy.tsx's checkPostTestData) and the wizard's own
// Previous/Next footer, since ViewTestAndControlMarket itself has no wizard
// navigation. ViewTestAndControlMarket.tsx is NOT modified.
const StepPowerAnalysis: React.FC<StepPowerAnalysisProps> = ({
  jobConfigId,
  jobName,
  isSales,
  onNext,
  onPrevious,
}) => {
  const [testMarkets, setTestMarkets] = useState<string[]>([]);
  const [selectedMarket, setSelectedMarket] = useState<string>("");
  const [allMarketData, setAllMarketData] = useState<Record<string, MarketData[]>>({});
  const [marketData, setMarketData] = useState<MarketData[]>([]);

  useEffect(() => {
    fetchTestMarkets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobConfigId]);

  const fetchTestMarkets = async () => {
    if (!jobConfigId) return;
    try {
      const response = await axiosInstance.get(`/api/geo-lift/output/${jobConfigId}`);
      const result = response.data?.result;
      if (!result) return;

      const locations = Object.keys(result)
        .filter((key) => key.includes("/BestMarkets"))
        .map((key) => key.split("/")[0]);

      if (locations.length > 0) {
        setTestMarkets(locations);
        setSelectedMarket(locations[0]);

        const marketDataMap: Record<string, MarketData[]> = {};
        locations.forEach((location) => {
          const key = `${location}/BestMarkets`;
          if (result[key]) {
            marketDataMap[location] = result[key];
          }
        });
        setAllMarketData(marketDataMap);
        setMarketData(marketDataMap[locations[0]] || []);
      }
    } catch (err) {
      console.error("Error fetching Causal Loop power analysis markets:", err);
    }
  };

  const handleMarketChange = (value: string) => {
    setSelectedMarket(value);
    setMarketData(allMarketData[value] || []);
  };

  return (
    <div className="flex flex-col">
      <ViewTestAndControlMarket
        testMarkets={testMarkets}
        selectedMarket={selectedMarket}
        marketData={marketData}
        jobConfigId={jobConfigId}
        preCampaignData={{
          geoLiftStudyName: jobName || "",
          buyType: "national",
          fileList: [],
        }}
        onMarketChange={handleMarketChange}
        onTestMarketsChange={setTestMarkets}
        buyType="national"
        mode={null}
        refreshTrigger={0}
        isSales={isSales}
      />

      <div className="flex justify-between mt-6">
        <Button size="large" onClick={onPrevious}>
          Previous
        </Button>
        <Button type="primary" size="large" onClick={onNext}>
          Next
        </Button>
      </div>
    </div>
  );
};

export default StepPowerAnalysis;
